import { client } from "frontend/api/client";
import { usePusher } from "frontend/hooks/use-pusher";
import { atom, useAtomValue } from "jotai";
import { atomFamily } from "jotai/utils";
import { createContext, ReactNode, useContext } from "react";
import { DebounceFetchRequests } from "../services/fetch-debounced";
import { MondayItemManager } from "../services/monday-item-manager";
import { ListKey } from "../services/monday-item.types";
import { MondayItemData, MondayItemError } from "shared/integrations/monday-types";

function buildItemsManager(documentId: string) {
  const batchedItemsFetcher = new DebounceFetchRequests(async (keys: string[]) => {
    const itemIds = keys;

    const items: Array<MondayItemData | MondayItemError> = await client
      .post(`/integrations/v2/board/${documentId}/items`, { itemIds })
      .then((res) => res.data);

    // Convert array response to object keyed by item ID
    const data: Record<string, MondayItemData | MondayItemError> = {};
    for (const item of items) {
      data[item.id] = item;
    }

    return data;
  });

  const manager = new MondayItemManager(
    {
      fetchItem: (itemId: string) => batchedItemsFetcher.fetch(itemId),
      updateItem: (itemId: string, patch: any, boardId?: string) => {
        return client.put(`/integrations/v2/item/${itemId}`, {
          data: patch,
          boardId,
        });
      },
    },
    {
      fetchLiveList(key: ListKey): Promise<{ items: any[]; cursor: string | undefined }> {
        return client
          .get("integrations/board/items_by_values", {
            params: {
              documentId,
              integrationId: key.integrationId,
              filters: key.filters,
            },
          })
          .then((res) => res.data);
      },
      fetchLiveListNextPage(key: ListKey, cursor: string): Promise<{ items: any[]; cursor: string | undefined }> {
        return client
          .get("integrations/board/items_by_values", {
            params: {
              documentId,
              integrationId: key.integrationId,
              cursor,
            },
          })
          .then((res) => res.data);
      },
    }
  );

  return manager;
}

const mondayItemManagersAtom = atomFamily((documentId: string) => atom(buildItemsManager(documentId)));

interface PusherEvent {
  integrationId: string;
  data: {
    itemId: string;
    name?: string;
    columnValues?: { id: string; type: string; value: any }[];
  };
}

/**
 * Pusher events are very compact, and sometimes the operation isn't obvious.
 * Creating a new item in a board will send and event like this
 *   >  {integrationId: "31764066", data :{"itemId":9702843232}}
 * no name and no column values, you just have to know it's about creation
 *
 * Deleting an item sends an event like this:
 *   >  {integrationId: "31764091", data :{}}
 * even worse...
 *
 * Changing a name will send itemId and name
 * Changing multiple items in monday will send multiple events - one for each item
 *
 */
function handlePusherEvent(manager: MondayItemManager | undefined, event: PusherEvent) {
  if (!event?.data || !manager) {
    return;
  }

  const { itemId, name, columnValues } = event?.data || {};
  if (!itemId) {
    return;
  }
  manager.updateItemFromPusher(String(itemId), { name, columnValues });
}

const MondayItemContext = createContext<MondayItemManager | undefined>(undefined);

export const MondayItemManagerProvider = ({
  documentId,
  pusherChannel,
  children,
}: {
  documentId: string | null | undefined;
  pusherChannel?: any;
  children: ReactNode;
}) => {
  const value = useAtomValue(mondayItemManagersAtom(documentId || ""));

  usePusher(
    pusherChannel,
    ["integration_data_changed", "live_integration_data_changed"],
    (data: PusherEvent) => handlePusherEvent(value, data),
    [pusherChannel, value]
  );

  return <MondayItemContext.Provider value={value}>{children}</MondayItemContext.Provider>;
};

export function useMondayItemManager() {
  const context = useContext(MondayItemContext);
  if (!context) {
    throw new Error("useMondayItemManager must be used inside a MondayItemManagerProvider");
  }
  return context;
}

export function useMondayItemManager_unsafe() {
  return useContext(MondayItemContext);
}
